import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, ArrowLeft, RefreshCw, Send, MessageSquare, Loader2 } from "lucide-react";
import { useOtpStatus, useRequestOtp, useVerifyOtp } from "../hooks/useClientAuth";
import { useClientAuth } from "../ClientAuthContext";
import { useBookingDraft } from "../../booking/BookingDraftContext";
import { useTranslation } from "react-i18next";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import {
    codeChannels, fallbackChannel, firstChannel, stillWaiting, undelivered, viaOf,
    STATUS_POLL_LIMIT, STATUS_POLL_MS, type CodeChannel, type CodeStatus, type CodeVia,
} from "../../../lib/codeChannels";
import type { PendingProof } from "../types";

interface Props {
    pending: PendingProof;
    onClose: () => void;
    /** Back to the form, filled in — the way out of a mistyped number. */
    onBack: () => void;
}

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // seconds

// When the last code went to this number, per channel. Reopening the screen
// (back, close, reload) must not fire another code — each remount used to send
// one, five in half a minute to a stranger's number in the 2026-09-28 support
// case. Per channel: asking for the SMS right after the Telegram code is the
// fallback, not a repeat.
const sentKey = (phone: string, channel: CodeChannel) => `client_otp_sent:${phone}:${channel}`;
const channelKey = (phone: string) => `client_otp_channel:${phone}`;

function secondsSinceSent(phone: string, channel: CodeChannel): number {
    try {
        const at = Number(sessionStorage.getItem(sentKey(phone, channel)));
        return at ? Math.floor((Date.now() - at) / 1000) : Infinity;
    } catch {
        return Infinity;
    }
}

function markSent(phone: string, channel: CodeChannel) {
    try {
        sessionStorage.setItem(sentKey(phone, channel), String(Date.now()));
        sessionStorage.setItem(channelKey(phone), channel);
    } catch { /* private mode: the cooldown just resets on reload */ }
}

/** The channel of a code sent within the cooldown — the screen reopens on it. */
function recentChannel(phone: string): CodeChannel | null {
    try {
        const ch = sessionStorage.getItem(channelKey(phone)) as CodeChannel | null;
        return ch && secondsSinceSent(phone, ch) < RESEND_COOLDOWN ? ch : null;
    } catch {
        return null;
    }
}

function forgetSent(phone: string) {
    try {
        for (const ch of ["telegram", "sms"] as CodeChannel[]) sessionStorage.removeItem(sentKey(phone, ch));
        sessionStorage.removeItem(channelKey(phone));
    } catch { /* ignore */ }
}

export default function ClientOtpView({ pending, onClose, onBack }: Props) {
    const { t, i18n } = useTranslation();
    // Telegram first — Boona pays for the codes — then SMS where the salon has it
    const channels = React.useMemo(
        () => codeChannels(getCompanyRegistry().getSettings()?.client_code_channels), []);
    const [channel, setChannel] = React.useState<CodeChannel>(
        () => recentChannel(pending.phone) ?? firstChannel(channels));
    const [code, setCode] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);
    const [resendCooldown, setResendCooldown] = React.useState(() =>
        Math.max(0, RESEND_COOLDOWN - secondsSinceSent(pending.phone, channel)));
    const [otpRequested, setOtpRequested] = React.useState(() =>
        secondsSinceSent(pending.phone, channel) < RESEND_COOLDOWN);
    // Every code is sent by the central worker a moment later; the screen asks
    // until it hears, and which app the code went to — the SMS step goes by
    // WhatsApp or Viber where that is cheaper (a reopened screen asks too).
    const [delivery, setDelivery] = React.useState<CodeStatus | null>(() =>
        otpRequested ? "pending" : null);
    const [via, setVia] = React.useState<CodeVia | null>(null);
    const [polls, setPolls] = React.useState(0);

    const requestOtpMutation = useRequestOtp();
    const otpStatusMutation = useOtpStatus();
    const verifyOtpMutation = useVerifyOtp();
    const { actions } = useClientAuth();
    const { draft } = useBookingDraft();
    const companyId = draft.companyId ?? 0;
    const phone = pending.phone;

    // Auto-request OTP on mount (unless one went out within the cooldown)
    React.useEffect(() => {
        if (!otpRequested && phone && companyId) {
            handleRequestOtp();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phone, companyId, otpRequested]);

    // Countdown timer for resend
    React.useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setInterval(() => {
            setResendCooldown((prev) => Math.max(0, prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);

    // Ask how the code is doing until the worker has answered
    React.useEffect(() => {
        if (!delivery || !stillWaiting(delivery) || polls >= STATUS_POLL_LIMIT || !companyId) return;
        const timer = setTimeout(async () => {
            try {
                const res = await otpStatusMutation.mutateAsync({ phone, company_id: companyId });
                if (res.channel === channel && res.status !== "none") {
                    setDelivery(res.status);
                    setVia(viaOf(res.via, channel));
                }
            } catch { /* keep asking until the limit; the SMS link is there regardless */ }
            setPolls((n) => n + 1);
        }, STATUS_POLL_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, delivery, polls, phone, companyId]);

    const handleRequestOtp = async (want: CodeChannel = channel) => {
        setError(null);
        setOtpRequested(true);
        try {
            const res = await requestOtpMutation.mutateAsync({
                phone,
                company_id: companyId,
                channel: want,
                locale: i18n.language,
            });
            // a backend from before the choice sends every code by SMS
            const went: CodeChannel = res.channel ?? "sms";
            setChannel(went);
            markSent(phone, went);
            setResendCooldown(RESEND_COOLDOWN);
            setDelivery("pending");
            setVia(null);
            setPolls(0);
        } catch (e: any) {
            const detail = e?.response?.data?.detail;
            const status = e?.response?.status;

            if (status === 429) {
                setError(t("widget.client.otp.too_many_requests", "Too many requests. Please try again later."));
            } else if (status === 400 && detail === "Invalid or incomplete phone number") {
                // Backend phone gate: the number cannot receive SMS at all —
                // localize instead of echoing the EN backend string.
                setError(t("widget.common.validation.phone_invalid", "This phone number looks incomplete — please check it"));
            } else if (status === 400 && typeof detail === "string" && detail.startsWith("This way of sending the code")) {
                setError(t("widget.client.otp.send_error", "Could not send the code"));
            } else {
                setError(detail || e?.message || t("widget.client.otp.send_error", "Could not send the code"));
            }
        }
    };

    /** The other channel: sends at once, unless a code went that way within the cooldown. */
    const switchTo = (next: CodeChannel) => {
        setCode("");
        const since = secondsSinceSent(phone, next);
        if (since < RESEND_COOLDOWN) {
            setChannel(next);
            setResendCooldown(RESEND_COOLDOWN - since);
            setDelivery("pending");
            setVia(null);
            setPolls(0);
            return;
        }
        handleRequestOtp(next);
    };

    const handleVerify = async () => {
        if (code.length !== OTP_LENGTH) return;
        setError(null);
        try {
            // The confirmed code creates the account (or signs in) with the
            // form's details and returns the session — nothing exists before.
            const res = await verifyOtpMutation.mutateAsync({
                phone,
                code,
                company_id: companyId,
                name: pending.name,
                email: pending.email,
                password: pending.password,
            });
            forgetSent(phone);
            actions.login({
                accessToken: res.access_token,
                clientId: res.client_id,
                phone: res.phone,
                expiresAt: Date.now() + res.expires_in * 1000,
                isVerified: true,
                name: res.name,
            });
        } catch (e: any) {
            const detail = e?.response?.data?.detail;
            if (!detail || detail === "Неверный или истекший код") {
                setError(t("widget.client.otp.invalid", "Invalid or expired code"));
            } else {
                setError(detail);
            }
        }
    };

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);
        setCode(value);
    };

    const canResend = resendCooldown === 0 && !requestOtpMutation.isPending;
    const other = fallbackChannel(channels, channel);
    const failed = delivery !== null && undelivered(delivery);
    // after a minute without an answer, stop spinning: the SMS link stays
    const waiting = delivery !== null && stillWaiting(delivery) && polls < STATUS_POLL_LIMIT;
    // the SMS step, by the app the worker reports (a messenger where it is cheaper than SMS)
    const app: CodeVia = via ?? channel;

    return (
        <div className="bg-background h-[100dvh] min-h-[100dvh]">
            <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
                <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-border/10 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={onBack}>
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <h2 className="text-lg font-semibold">{t("widget.client.otp.title", "Phone verification")}</h2>
                        </div>
                        <Button variant="ghost" size="icon" onClick={onClose}>
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        <div className="text-center space-y-2">
                            <div className="mx-auto w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                                {channel === "telegram" ? <Send className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
                            </div>
                            <p className="text-muted-foreground">
                                {channel === "telegram"
                                    ? (failed
                                        ? t("widget.client.otp.tg_not_sent", "The code could not go to Telegram for")
                                        : waiting
                                            ? t("widget.client.otp.tg_sending", "Sending a code to Telegram for")
                                            : t("widget.client.otp.tg_sent_to", "We sent a code to Telegram for"))
                                    : failed
                                        ? t("widget.client.otp.sms_not_sent", "The code could not be sent to")
                                        : waiting
                                            ? t("widget.client.otp.sending_to", "Sending a code to")
                                            : app === "whatsapp"
                                                ? t("widget.client.otp.wa_sent_to", "We sent a code to WhatsApp for")
                                                : app === "viber"
                                                    ? t("widget.client.otp.viber_sent_to", "We sent a code to Viber for")
                                                    : app === "telegram"
                                                        ? t("widget.client.otp.tg_sent_to", "We sent a code to Telegram for")
                                                        : t("widget.client.otp.sent_to", "We sent an SMS code to")}
                            </p>
                            <p className="font-semibold text-lg">{phone}</p>
                            {channel === "telegram" && !failed && (
                                <p className="text-sm text-muted-foreground">
                                    {t("widget.client.otp.tg_where", "Open Telegram — the code comes from «Verification Codes».")}
                                </p>
                            )}
                            {channel === "sms" && !failed && !waiting && (app === "whatsapp" || app === "viber") && (
                                <p className="text-sm text-muted-foreground">
                                    {app === "whatsapp"
                                        ? t("widget.client.otp.wa_where", "Open WhatsApp. No WhatsApp? The code comes by SMS.")
                                        : t("widget.client.otp.viber_where", "Open Viber. No Viber? The code comes by WhatsApp or SMS.")}
                                </p>
                            )}
                            <button
                                type="button"
                                className="text-sm text-primary underline-offset-2 hover:underline"
                                onClick={onBack}
                            >
                                {t("widget.client.otp.change_number", "Wrong number? Change it")}
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground">
                                    {t("widget.client.otp.enter_code", "Enter the 6-digit code")}
                                </Label>
                                <Input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="______"
                                    value={code}
                                    onChange={handleCodeChange}
                                    maxLength={6}
                                    className="text-center text-2xl tracking-[0.5em] font-mono"
                                    autoFocus
                                />
                            </div>

                            {error && (
                                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                    {error}
                                </div>
                            )}

                            {failed && channel === "telegram" && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
                                    <p>
                                        {delivery === "no_telegram"
                                            ? t("widget.client.otp.no_telegram", "This number has no Telegram account.")
                                            : t("widget.client.otp.tg_failed", "We could not send the code to Telegram.")}
                                        {" "}
                                        {other === "sms"
                                            ? t("widget.client.otp.try_sms", "Get it by SMS instead.")
                                            : t("widget.client.otp.check_number", "Check the number, or try again later.")}
                                    </p>
                                    {other === "sms" && (
                                        <Button className="w-full rounded-xl" onClick={() => switchTo("sms")}
                                            disabled={requestOtpMutation.isPending}>
                                            <MessageSquare className="w-4 h-4 mr-2" />
                                            {t("widget.client.otp.send_sms", "Send the code by SMS")}
                                        </Button>
                                    )}
                                </div>
                            )}

                            {failed && channel === "sms" && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                    {t("widget.client.otp.check_number", "Check the number, or try again later.")}
                                </div>
                            )}
                        </div>

                        {/* Resend section */}
                        <div className="text-center space-y-2">
                            {waiting && (
                                <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t("widget.client.otp.tg_waiting", "Sending…")}
                                </p>
                            )}
                            {failed && channel === "telegram" && other ? null : resendCooldown > 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    {t("widget.client.otp.resend_in", "Send code again in {{count}} sec", { count: resendCooldown })}
                                </p>
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRequestOtp()}
                                    disabled={!canResend}
                                    className="text-primary"
                                >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${requestOtpMutation.isPending ? "animate-spin" : ""}`} />
                                    {t("widget.client.otp.resend", "Send code again")}
                                </Button>
                            )}
                            {/* Telegram first; SMS is one click away where the salon has it */}
                            {channel === "telegram" && other === "sms" && !failed && (
                                <div>
                                    <button
                                        type="button"
                                        className="text-sm text-primary underline-offset-2 hover:underline disabled:opacity-50"
                                        onClick={() => switchTo("sms")}
                                        disabled={requestOtpMutation.isPending}
                                    >
                                        {t("widget.client.otp.sms_instead", "No Telegram? Get the code by SMS")}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer CTA */}
                    <div
                        className="sticky bottom-0 inset-x-0 z-20 border-t border-border/10 bg-card/95 backdrop-blur"
                        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
                    >
                        <div className="p-4 pt-3">
                            <Button
                                className="w-full h-12 rounded-xl font-semibold"
                                onClick={handleVerify}
                                disabled={verifyOtpMutation.isPending || code.length !== OTP_LENGTH}
                            >
                                {verifyOtpMutation.isPending ? t("widget.client.otp.verifying", "Verifying...") : t("widget.client.otp.submit", "Confirm")}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
